import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Container, Grid, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import LazyLoader from "../components/LazyLoader";
import useSnackbar from "../components/snackbar/useSnackBar";
import { useApi } from "../hooks/useApi";

/**
 * Implements the Blacklist-Overview.
 * @returns 
 */
export default function Blacklist() {
  const [data, setData] = useState([]);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const [endReached, setEndReached] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [value, setValue] = useState("");

  const navigate = useNavigate();

  const { openSnackBar } = useSnackbar();

  const intl = useIntl();

  const api = useApi();

  /**
   * Fetch everything except the first page, whenever the page changes.
   */
  useEffect(() => {
    if (page === 1) return;
    fetchData(page);
  }, [page]);

  /**
   * Start the fetch in the beginning.
   */
  useEffect(() => {
    startFetch();
  }, []);

  /**
   * Starts a clean new fetch.
   */
  const startFetch = () => {
    setEndReached(false);
    setPage(1);
    fetchData(1);
  }

  /**
   * Fetches the specified page.
   * @param {*} page 
   */
  const fetchData = (page) => {
    setIsLoading(true);
    api
      .get(`/blacklist/filter?page=${page - 1}&size=${size}`)
      .then(response => {
        if (page === 1) {
          setData(response.data.content);
        } else {
          setData(old => [...old, ...response.data.content]);
        }
        setEndReached(response.data.last);
      })
      .catch(error => {
        console.log(error);
        if (error.response.status === 403) {
          navigate('/login');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * The data-elements in Grid-layout.
   */
  const dataElements = useMemo(() => {
    return data.map(d => {
      return (
        <Grid key={d.id} item container xs={12} sx={{ alignItems: 'center' }} >
          <Grid item xs={6} md={3} >
            <p style={{ fontSize: '20px' }} >{d.name}</p>
          </Grid>
          <Grid item xs={6} md={3} >
            <Button
              component={Link}
              onClick={() => deleteEntry(d.id)}
              color="error"
              aria-label="delete"
            >
              <DeleteIcon />
            </Button>
          </Grid>
          <Grid
            item xs={12} style={{ height: '1px', backgroundColor: 'grey' }}
          ></Grid>
        </Grid>
      )
    });
  }, [data]);

  /**
   * Adds an entry to the blacklist on the server.
   * @param {*} value 
   */
  const addEntry = (value) => {
    if (!value) return;
    api
      .post(`/blacklist`, { name: value })
      .then(() => {
        setValue("");
        // reload blacklist
        startFetch();
      })
      .catch(error => {
        console.log(error);
        if (error.response.status === 500) {
          openSnackBar(intl.formatMessage({ id: 'Blacklist.addingFailed' }));
        }
      });
  };

  /**
   * Deletes the specified entry from the blacklist on the server.
   * @param {*} id 
   */
  const deleteEntry = (id) => {
    api
      .delete(`/blacklist/${id}`)
      .then(() => {
        // reload blacklist
        startFetch();
      })
      .catch(error => {
        console.log(error);
      });
  };

  return (
    <Container>
      <h3><FormattedMessage id="Blacklist.heading" /></h3>
      <p style={{ color: 'gray' }} ><FormattedMessage id="Blacklist.forWhat" /></p>

      <Grid container style={{ width: '100%' }} sx={{ alignItems: 'center' }} spacing={1} >
        <Grid item xs={12} md={4} >
          <TextField
            style={{ width: '100%' }}
            size="small"
            label={"Name"}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.code === "Enter") {
                addEntry(event.target.value);
              }
            }}
            inputProps={{ maxLength: 50 }}
          />
        </Grid>
        <Grid item xs={12} md={3} >
          <Button
            variant="contained"
            onClick={() => addEntry(value)}
          >
            <FormattedMessage id="Blacklist.addEntry" />
          </Button>
        </Grid>
      </Grid>

      <LazyLoader
        onLoad={() => setPage(prev => prev + 1)}
        loading={isLoading}
        endReached={endReached}
      >
        <Grid container spacing={1} >
          {dataElements}
        </Grid>
      </LazyLoader>
    </Container>
  )
};